/* eslint-disable no-console */
import axios, { AxiosInstance } from 'axios';

import { ApiException } from '@mse-reg/clients';
import { SERVER_API_URL } from '@mse-reg/config';
import * as Sentry from '@sentry/browser';
import { appActions } from '../modules/application';
import { authActions } from '../modules/login/auth.slice';
import { addError, addExceptionNotification } from '../modules/notification.slice';
import { AppDispatch } from './store-types';

const TIMEOUT = 2 * 60 * 1000;
axios.defaults.timeout = TIMEOUT;
axios.defaults.baseURL = SERVER_API_URL;
const isDev = process.env.NODE_ENV === 'development';

class IdentifiableApiException extends ApiException {
  level: 'error' | 'warning' | 'info' | 'success';
  type: string;
  id: number;
  constructor(message: string, status: number, response: string, headers: { [key: string]: any }, result: any) {
    super(message, status, response, headers, result);
    this.level = 'error';
    this.type = 'IdentifiableApiException';
    this.id = Date.now() + Math.random();
  }
}

class MessageException extends IdentifiableApiException {
  additionalData: { [key: string]: any };
  constructor(
    message: string,
    level: 'error' | 'warning' | 'info' | 'success',
    status: number,
    response: string,
    headers: { [key: string]: any },
    result: any,
    additionalData: { [key: string]: any },
  ) {
    super(message, status, response, headers, result);
    this.level = level;
    this.type = 'MessageException';
    this.id = Date.now() + Math.random();
    this.additionalData = additionalData;
  }
}

const readBlobData: (data: Blob) => Promise<MessageException> = (data) => {
  return new Promise<MessageException>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const dataObject = JSON.parse(reader.result.toString());
        if (dataObject.type === 'Message') {
          const message = new MessageException(
            dataObject.message,
            dataObject.level,
            dataObject.status,
            null,
            null,
            null,
            dataObject.additionalData,
          );
          resolve(message);
        } else {
          resolve(null);
        }
      } catch {
        reject(new Error('Could not parse the message object!'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Could not parse the message object! Invalid blob data!'));
    };

    reader.readAsText(data);
  });
};

const getData = (err: any) => {
  if (err.response?.data) {
    if (typeof err.response.data === 'string') {
      try {
        return JSON.parse(err.response.data);
      } catch {
        return err.response.data;
      }
    }
    return err.response.data;
  }
  return null;
};

const logError = (err: any, message?: any) => {
  if (isDev) {
    console.log('Error', message || err);
  } else {
    Sentry.captureException(message || err);
  }
};

const handleMessageType = (data: any, err: any) => {
  const messageException = new MessageException(
    data.extensions?.message ?? data.message,
    data.extensions?.level ?? data.level,
    data.statusCode,
    err.response,
    err.headers,
    err.result,
    [],
  );
  logError(err, messageException);
  throw messageException;
};

const handleProblemJson = async (data: any, err: any, dispatch: AppDispatch) => {
  try {
    const message = await readBlobData(data);
    if (message !== null) {
      message.response = err.response;
      message.headers = err.headers;
      message.result = err.result;
      dispatch(addExceptionNotification(message));
      if (message.level !== 'error') {
        return;
      }
      logError(err);
      throw message;
    }
  } catch (parseError) {
    logError(err);
    if (isDev) {
      console.log('ParseError', parseError);
    } else {
      Sentry.captureException(parseError);
    }
    throw err;
  }
  logError(err);
  const messageException = new MessageException(
    'An unknown error occurred!',
    'error',
    0,
    err.response,
    err.headers,
    err.result,
    [],
  );
  dispatch(addExceptionNotification(messageException));
  throw err;
};

const handleErrorInData = (data: any, err: any, dispatch: AppDispatch) => {
  logError(err);
  const exception = new MessageException(
    err.response.data.error,
    'error',
    err.response.status,
    err.response,
    err.headers,
    err.result,
    err.response.data.additionalData,
  );
  dispatch(addExceptionNotification(exception));
  throw err;
};

const handleStatusCode = (status: number, err: any, dispatch: AppDispatch) => {
  switch (status) {
    case 403:
    case 401:
      dispatch(addError('You are not authorized!'));
      dispatch(authActions.clearAuth());
      throw new IdentifiableApiException('You are not authorized!', status, err.response, err.headers, err.result);
    case 404:
      dispatch(addError(`Resource not found!`));
      return null;
    case 504:
      logError(err);
      dispatch(appActions.addApplicationError(`Server not reachable!`));
      throw new IdentifiableApiException('Server not reachable!', status, err.response, err.headers, err.result);
    case 500: {
      logError(err);
      const messageException = new IdentifiableApiException(
        'An unknown error occurred on the server!',
        status,
        err.response,
        err.headers,
        err.result,
      );
      dispatch(addExceptionNotification(messageException));
      throw messageException;
    }
    default: {
      logError(err);
      const messageException = new IdentifiableApiException(
        'An unknown error occurred!',
        status,
        err.response,
        err.headers,
        err.result,
      );
      dispatch(addExceptionNotification(messageException));
      throw messageException;
    }
  }
};

const setupAxiosInterceptors = (instance: AxiosInstance, dispatch: AppDispatch) => {
  const onRequestSuccess = (config) => {
    return config;
  };
  const onResponseSuccess = (response) => {
    return response;
  };
  const onResponseError = async (err: any) => {
    const data = getData(err);
    if (data) {
      if (data.type === 'Message') {
        handleMessageType(data, err);
      }
      if (data?.type?.toString() === 'application/problem+json') {
        await handleProblemJson(data, err, dispatch);
      }
      if (data.error) {
        handleErrorInData(data, err, dispatch);
      }
    }

    if (isDev) {
      console.log('Error', err);
      dispatch(addExceptionNotification(err));
    }

    const status = err.status || (err.response ? err.response.status : 0);
    return handleStatusCode(status, err, dispatch);
  };
  instance.interceptors.request.use(onRequestSuccess);
  instance.interceptors.response.use(onResponseSuccess, onResponseError);
};

export default setupAxiosInterceptors;
